import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { toast } from "@/components/feedback/ToastHost";

/**
 * Live notification stream (SSE, Phase 7).
 *
 * Subscribes to `/api/v1/events/stream` and renders each pushed notification
 * as a toast (severity-mapped), substituting the event params into the
 * localized title. The browser reconnects automatically and the server resumes
 * via `Last-Event-ID`, so missed events are replayed on reconnect.
 */
export function useEventStream(enabled = true): void {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const source = new EventSource("/api/v1/events/stream");

    const onNotification = (event: MessageEvent) => {
      let payload: {
        id: number;
        event_type: string;
        severity: string;
        title_key: string;
        body_key: string;
        params?: Record<string, unknown>;
      };
      try {
        payload = JSON.parse(event.data) as typeof payload;
      } catch {
        return; // malformed frame — ignore
      }
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      const options = { duration: 6000 };
      const title = t(payload.title_key, { ...payload.params, defaultValue: payload.title_key });
      if (payload.severity === "error" || payload.severity === "critical") {
        toast.error(title, options);
      } else if (payload.severity === "warning") {
        toast.warning(title, options);
      } else {
        toast.info(title, options);
      }
    };

    source.addEventListener("notification", onNotification);
    return () => {
      source.removeEventListener("notification", onNotification);
      source.close();
    };
  }, [enabled, queryClient, t]);
}
